import { editDocument, getDocument } from '@/services/documents-service/documents-service';
import { UpdateDocumentPayload } from '@/services/documents-service/documents-service.types';
import { openNotification } from '@/utils/notification/notification';
import { ReactQueryKeys } from '@/utils/react-query-keys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';

export const useDocument = (id: string) => {
  const queryClient = useQueryClient();

  const { data: documentData, error } = useQuery({
    queryKey: ReactQueryKeys.DOCUMENT(id),
    queryFn: () => getDocument(id),
    enabled: Boolean(id),
  });

  const { mutate: updatedDocument, isPending: isPendingDocumentUpdate } = useMutation({
    mutationFn: (data: UpdateDocumentPayload) => editDocument(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.DOCUMENT_ALL(),
      });

      // The list holds its own copy of name and content, so an edit has to reach it too.
      queryClient.invalidateQueries({
        queryKey: ReactQueryKeys.DOCUMENTS_ALL(),
      });

      openNotification({
        message: t('documents:notifications.updated'),
        type: 'success',
      });
    },
    onError: (err) => {
      openNotification({
        message: t('documents:notifications.error'),
        type: 'error',
      });
      return err;
    },
  });

  const updateDocument = (data: UpdateDocumentPayload, callbacks?: { onSuccess?: () => void }) => {
    updatedDocument(data, {
      onSuccess: () => {
        if (callbacks?.onSuccess) {
          callbacks.onSuccess();
        }
      },
    });
  };

  return { documentData, error, updatedDocument: updateDocument, isPendingDocumentUpdate };
};
